import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { getById } from "@insite/client-framework/Store/Data/DataState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import refreshCertificatesAfterUpload from "@thkStore/Store/Widgets/ExemptionCertificateList/Handlers/RefreshCertificatesAfterUpload";
import {
    ApplicationExemptionCertificateWizardState,
} from "@thkStore/Store/Widgets/ExemptionCertificateWizard/ExemptionCertificateWizardReducer";
import loadEcwAuthentication, {
    EcwOAuthResponse,
} from "@thkStore/Store/Widgets/ExemptionCertificateWizard/Handlers/LoadEcwAuthentication";
import triggerCertificateSyncAndNotify from "@thkStore/Store/Widgets/ExemptionCertificateWizard/Handlers/TriggerCertificateSyncAndNotify";
import parse from "html-react-parser";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    ecwLibrary = "ecwLibrary",
    buttonLabel = "buttonLabel",
    informationalContent = "informationalContent",
    widthMode = "widthMode",
    buttonWidth = "buttonWidth",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.ecwLibrary]: string;
        [fields.buttonLabel]: string;
        [fields.informationalContent]: string;
        [fields.widthMode]: "fill" | "fixed";
        [fields.buttonWidth]: string;
    };
}

const mapStateToProps = (state: ApplicationExemptionCertificateWizardState) => ({
    isLoading: state.exemptionCertificateWizard.isLoading,
    error: state.exemptionCertificateWizard.error,
    customerCode: getById(state.data.billTos, state.context.session.billToId)?.value?.customerNumber,
});

const mapDispatchToProps = {
    loadEcwAuthentication: makeHandlerChainAwaitable<
        Parameters<typeof loadEcwAuthentication>[0],
        EcwOAuthResponse
    >(loadEcwAuthentication),
    loadCurrentBillTo,
    triggerCertificateSyncAndNotify,
    refreshCertificatesAfterUpload,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ExemptionCertificateWizard: React.FC<Props> = props => {
    const [scriptLoaded, setScriptLoaded] = React.useState(false);
    let ecwOAuthResponse : EcwOAuthResponse;

    
    useEffect(() => {
        // Declared at effect scope so the cleanup below removes exactly this listener
        // (and never leaves a duplicate behind when the effect re-runs on bill-to change).
        const handleWizardMessage = (e: MessageEvent) => {
            // A postMessage event's origin is scheme+host+port only (e.g. https://ecwportal.vertexsmb.com),
            // whereas wizardPath is a full URL with a path. Compare against the wizardPath's ORIGIN,
            // otherwise this guard always returns early and the on-demand sync/notify flow never runs.
            let wizardOrigin = "";
            try {
                const wizardPath = ecwOAuthResponse?.properties?.wizardPath || "";
                wizardOrigin = wizardPath ? new URL(wizardPath).origin : "";
            } catch {
                wizardOrigin = "";
            }

            // validate it's actually from the Vertex wizard before trusting it
            if (!wizardOrigin || e.origin !== wizardOrigin) {
                return;
            }

            if (e.data && e.data.type === "createdCertificates") {
                const certs = e.data.data; // array of created certs
                const uploadedUuids: string[] = [];
                certs.forEach(function (c: any) {
                    // Sync just this certificate from Vertex and run the (global, idempotent)
                    // change-notification job so the new pending cert's admin email goes out,
                    // instead of waiting for the scheduled jobs. Fire-and-forget; never blocks the UI.
                    if (c && c.id) {
                        props.triggerCertificateSyncAndNotify({ uuid: c.id });
                        uploadedUuids.push(c.id);
                    }
                });

                // The sync above is a queued batch job, so the new certificate is not in the
                // local DB yet. Poll the list (page 1, newest first) until it appears, showing
                // the "submitted for review / refreshing" notice so the user isn't confused.
                if (uploadedUuids.length > 0) {
                    props.refreshCertificatesAfterUpload({ uuids: uploadedUuids });
                }
            }
        };

        const initialize = async () => {

            if (!props.customerCode) {
                props.loadCurrentBillTo();
                return;
            }

            ecwOAuthResponse = await props.loadEcwAuthentication({});

            if (props.fields.ecwLibrary) {
                await loadEcwScript(props.fields.ecwLibrary);

                const vertexObject = (window as any)["vertex"];
                if (!vertexObject) {
                    console.error("ECW library did not load correctly. 'vertex' object not found on window.");
                }

                const instance = new vertexObject.Wizard({
                    domNode: document.getElementById('wizard-btn'),
                    wizardPath: ecwOAuthResponse.properties?.wizardPath || "",
                    accessToken: ecwOAuthResponse.accessToken,
                    buyerCode: props.customerCode,
                    sellerCodes: [ecwOAuthResponse.properties?.sellerCode || ""],
                    partitionUuid: ecwOAuthResponse.properties?.partitionUuid || ""
                });

                window.addEventListener("message", handleWizardMessage);
            }
        };

        initialize();

        return () => {
            window.removeEventListener("message", handleWizardMessage);
        };
    }, [props.customerCode]);

    const loadEcwScript = (src: string): Promise<void> => {        
        if (scriptLoaded) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = src;
            script.async = true;
            script.onload = () => {
                setScriptLoaded(true);
                resolve();
            };
            script.onerror = () => reject(new Error(`Failed to load ECW library from ${src}`));
            document.head.appendChild(script);
        });
    };

    // "fill" stretches the button to its container; "fixed" honors the admin-entered width
    // (falling back to fill-container if no value was provided).
    const buttonWidth =
        props.fields.widthMode === "fixed" && props.fields.buttonWidth ? props.fields.buttonWidth : "100%";

    return (
        <div style={{ width: "100%", paddingTop: "16px" }}>
            {props.fields.informationalContent && (
                <div style={{ marginBottom: "16px" }}>
                    {parse(props.fields.informationalContent, parserOptions)}
                </div>
            )}
            <Button
                disabled={props.isLoading}
                id="wizard-btn"
                variant="primary"
                mergeCss
                css={css`
                    width: ${buttonWidth};
                `}
            >
                {props.isLoading ? "Loading..." : props.fields.buttonLabel || "Manage Exemption Certificates"}
            </Button>
        </div>
    );
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ExemptionCertificateWizard as any),
    definition: {
        group: "Basic",
        icon: "shield",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.ecwLibrary,
                editorTemplate: "TextField",
                displayName: "ECW Library",
                defaultValue: "https://ccwizard.vertexsmb.com/wizard/button.js",
                tooltip: "URL to the Exemption Certificate Wizard JavaScript library",
                tab: basicTab,
                isRequired: true,
            },
            {
                fieldType: "General",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "Manage Exemption Certificates",
                tooltip: "Label displayed on the button that opens the ECW",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.informationalContent,
                editorTemplate: "RichTextField",
                displayName: "Informational Content",
                defaultValue: "",
                tooltip: "Rich text content displayed above the wizard button to provide relevant information to the user",
                extendedConfig: { height: 200 },
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.widthMode,
                editorTemplate: "DropDownField",
                displayName: "Button Width",
                options: [
                    { value: "fill", displayName: "Fill Container" },
                    { value: "fixed", displayName: "Fixed Width" },
                ],
                defaultValue: "fill",
                tooltip:
                    "Fill Container stretches the button to the width of the area it is placed in. Fixed Width keeps it at a specific size.",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.buttonWidth,
                editorTemplate: "TextField",
                displayName: "Fixed Width Value",
                defaultValue: "300px",
                tooltip: "CSS width applied when Button Width is set to Fixed Width, e.g. 300px or 50%.",
                tab: basicTab,
                isVisible: item => item.fields[fields.widthMode] === "fixed",
            },
        ],
    },
};

export default widgetModule;
